import type { EntityDefinition, RelationDefinition } from "@/shared/contracts/structure";
import { instantOf } from "@/shared/format";
import { optionsFor } from "./field-config";
import { isComputedField } from "./field-widget";
import { recordFields } from "./record-fields";

/**
 * O PREFIXO dos valores iniciais no endereço: `?novo=1&campo.<código>=<valor>`.
 *
 * Prefixado e não `?<código>=` solto porque a página já lê a query para outras
 * coisas (`registro`, `painel`, `novo`…): um campo chamado `painel` abriria um
 * painel em vez de preencher um valor.
 */
export const CREATE_DEFAULT_PREFIX = "campo.";

/**
 * `?volta=<caminho>`: para onde o formulário de criação devolve a pessoa ao fechar
 * (o botão `opens` de um registro). Só caminho interno — `//x` ou um endereço
 * completo levaria para fora do painel; o que não serve é "".
 */
export const RETURN_PARAM = "volta";
export function returnPath(raw: unknown): string {
	const to = typeof raw === "string" ? raw : "";
	return to.startsWith("/") && !to.startsWith("//") && !to.startsWith("/\\") ? to : "";
}

/** Tipos que um endereço não preenche: um arquivo, uma assinatura, um JSON cru. */
const NOT_PREFILLABLE = new Set(["file", "signature", "json"]);

function texts(raw: unknown): string[] {
	const list = Array.isArray(raw) ? raw : [raw];
	return list.filter((v): v is string => typeof v === "string" && v.trim() !== "");
}

/** Uma data real, não só o desenho de uma: "2026-02-31" não passa. */
function isCalendarDate(text: string): boolean {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return false;
	const at = new Date(`${text}T12:00:00Z`);
	return !Number.isNaN(at.getTime()) && at.toISOString().slice(0, 10) === text;
}

/**
 * Com fuso, é um instante; sem fuso, é o relógio do WORKSPACE (o mesmo que o
 * widget mostra). Só a data não basta: "chegada às 00:00" seria um valor
 * inventado que ninguém escolheu.
 */
function instantFrom(text: string): string | undefined {
	if (/(Z|[+-]\d{2}:?\d{2})$/.test(text)) {
		const at = new Date(text);
		return Number.isNaN(at.getTime()) ? undefined : at.toISOString();
	}
	return isCalendarDate(text.slice(0, 10)) ? (instantOf(text) ?? undefined) : undefined;
}

/** As opções pedidas que o campo tem; sem lista estática (opções de fonte), todas. */
function chosen(config: Record<string, unknown>, values: string[]): string[] {
	const options = optionsFor(config);
	const picked = values
		.map((v) => v.trim())
		.filter((v) => options.length === 0 || options.some((o) => o.value === v));
	return [...new Set(picked)];
}

/**
 * Um valor do endereço → o valor que o widget do tipo espera, ou `undefined`.
 *
 * A regra de verdade continua no servidor, no salvar; isto só recusa o que o
 * formulário não conseguiria MOSTRAR (um "abc" num número, um datetime sem
 * hora) — melhor um campo em branco do que um valor que a pessoa não vê
 * errado até o servidor o recusar.
 */
function coerce(
	type: string,
	config: Record<string, unknown>,
	values: string[],
): unknown | undefined {
	const first = values[0]?.trim();
	if (first === undefined) return undefined;
	switch (type) {
		case "number":
		case "currency":
		case "rating": {
			const n = Number(first);
			return Number.isFinite(n) ? n : undefined;
		}
		case "integer": {
			const n = Number(first);
			return Number.isSafeInteger(n) ? n : undefined;
		}
		case "boolean":
			if (first === "true" || first === "1") return true;
			if (first === "false" || first === "0") return false;
			return undefined;
		case "date":
			return isCalendarDate(first) ? first : undefined;
		case "time":
			return /^\d{2}:\d{2}(:\d{2})?$/.test(first) ? first : undefined;
		case "datetime":
			return instantFrom(first);
		case "select":
			return chosen(config, [first])[0];
		case "multi_select": {
			const picked = chosen(config, values);
			return picked.length > 0 ? picked : undefined;
		}
		default:
			return first;
	}
}

/**
 * Os valores com que um "novo registro" nasce, lidos do endereço.
 *
 * Só campos que o formulário de criação mostra e aceita: vivos, não
 * calculados, e nunca a etapa (quem nasce num funil nasce na entrada dele — a
 * etapa não é escolha do formulário de criação). Código desconhecido e valor
 * que não é do tipo SAEM em silêncio: o formulário abre na mesma, com o resto.
 */
export function createDefaultsFromQuery(
	entity: EntityDefinition,
	relations: readonly RelationDefinition[],
	query: Record<string, unknown>,
): Record<string, unknown> {
	const byCode = new Map(recordFields(entity, relations).map((field) => [field.code, field]));
	const defaults: Record<string, unknown> = {};
	for (const [key, raw] of Object.entries(query)) {
		if (!key.startsWith(CREATE_DEFAULT_PREFIX)) continue;
		const field = byCode.get(key.slice(CREATE_DEFAULT_PREFIX.length));
		if (!field || field.code === "stage" || isComputedField(field)) continue;
		if (NOT_PREFILLABLE.has(field.type)) continue;
		const value = coerce(field.type, field.config, texts(raw));
		if (value !== undefined) defaults[field.code] = value;
	}
	return defaults;
}
